import { useEffect, useState } from 'react';
import * as THREE from 'three';
import logoUrl from '../../assets/tipsy-logo.webp';

export interface TruckTextures {
  roundel: THREE.CanvasTexture;
  sideSign: THREE.CanvasTexture;
  logoTex: THREE.Texture;
  logoAspect: number;
}

function makeCanvasTex(w: number, h: number, draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  draw(c.getContext('2d')!, w, h);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  return t;
}

function buildTextures(img: HTMLImageElement): TruckTextures {
  const lw = img.width;
  const lh = img.height;

  // Door / rear roundel: cream disc + the top ~74% of the logo (the wordmark only)
  const roundel = makeCanvasTex(512, 512, (x, w, h) => {
    x.clearRect(0, 0, w, h);
    x.fillStyle = '#f7eee9';
    x.beginPath();
    x.arc(w / 2, h / 2, w / 2 - 6, 0, Math.PI * 2);
    x.fill();
    const cropH = lh * 0.74;
    const iw = w * 0.72;
    const ih = (iw * cropH) / lw;
    x.drawImage(img, 0, 0, lw, cropH, (w - iw) / 2, (h - ih) / 2, iw, ih);
  });

  // Side signage: cream rounded plaque + full logo — reads on any body colour
  const sideSign = makeCanvasTex(1024, 680, (x, w, h) => {
    x.clearRect(0, 0, w, h);
    x.fillStyle = '#f7eee9';
    x.beginPath();
    x.roundRect(30, 30, w - 60, h - 60, 56);
    x.fill();
    const iw = w * 0.74;
    const ih = (iw * lh) / lw;
    x.drawImage(img, (w - iw) / 2, (h - ih) / 2, iw, ih);
  });

  const logoTex = new THREE.Texture(img);
  logoTex.colorSpace = THREE.SRGBColorSpace;
  logoTex.needsUpdate = true;

  return { roundel, sideSign, logoTex, logoAspect: lw / lh };
}

/**
 * Loads the navy-on-transparent Tipsy logo imperatively and derives the
 * branding textures. Returns null until the image decodes (a few ms) — this
 * avoids a Suspense boundary, which keeps hook order stable under StrictMode.
 */
export function useTruckTextures(): TruckTextures | null {
  const [tex, setTex] = useState<TruckTextures | null>(null);
  useEffect(() => {
    let alive = true;
    const img = new Image();
    img.onload = () => {
      if (alive) setTex(buildTextures(img));
    };
    img.src = logoUrl;
    return () => {
      alive = false;
    };
  }, []);
  return tex;
}
